import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { getAllTenantProvisioningHealthChecks } from '../api/tenantProvisioningApi';
import { DataTable } from '../components/DataTable';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';
import { PageShell } from '../components/PageShell';
import { StatusBadge } from '../components/StatusBadge';

export function TenantProvisioningHealthChecksPage() {
  const { t } = useTranslation(['tenantProvisioning', 'common']);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllTenantProvisioningHealthChecks({ per_page: 100 });
      setRows(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const columns = useMemo(() => [
    { id: 'tenant_id', label: t('tenantProvisioning:health.columns.tenant'), render: (row) => <RouterLink to={`/tenants/${row.tenant_id}`}>{t('tenantProvisioning:dashboard.tenant_link', { id: row.tenant_id })}</RouterLink> },
    { id: 'status', label: t('common:status'), render: (row) => <StatusBadge status={row.status} category="health_check" /> },
    { id: 'connection_ok', label: t('tenantProvisioning:health.columns.connection'), render: (row) => (row.connection_ok ? t('statuses:yes') : t('statuses:no')) },
    { id: 'message', label: t('common:message') },
    { id: 'checked_at', label: t('tenantProvisioning:health.columns.checked_at') },
  ], [t]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={t('tenantProvisioning:health.load_failed')} onRetry={load} />;

  return (
    <PageShell title={t('tenantProvisioning:health.title')} subtitle={t('tenantProvisioning:health.subtitle')}>
      <DataTable columns={columns} rows={rows} rowKey="id" emptyMessage={t('tenantProvisioning:health.no_checks')} />
    </PageShell>
  );
}
